<template>
  <div>
    <!-- Unprocessed audio/video preview -->
    <v-layout
      v-if="previewedSpot && !previewedSpot.processed && !useIframePreview"
      justify-center
    >
      <v-layout
        justify-center
        align-center
        class="grey--text text--darken-1 body-2"
        :class="{
          'vignette-shadow': previewedSpot.ctype == 'tv',
          'py-3': previewedSpot.ctype == 'radio',
        }"
        :style="{
          'max-height': tvPreviewMaxDimensions.height + 'px',
          'max-width': tvPreviewMaxDimensions.width + 'px',
          'min-height': tvPreviewMaxDimensions.height + 'px',
          'min-width': tvPreviewMaxDimensions.width + 'px',
        }"
      >
        <v-layout
          column
          align-center
        >
          <div
            v-if="bundle && bundle.has_multiple_formats"
            class="my-3"
          >
            {{ $t('bundledContentFormatsHint') }}
          </div>
          <v-icon
            v-if="previewedSpot.ctype == 'tv'"
            large
            color="grey darken-1"
            class="mb-3"
          >
            tv
          </v-icon>
          {{ $t('previewAvailableSoon') }}
        </v-layout>
      </v-layout>
    </v-layout>
    <div
      v-else
      id="players"
      class="mt-2"
    >
      <!-- TVSpot Preview -->
      <!-- Player with max. size avoids blurry playback on large devices -->
      <v-layout
        v-if="$route.params.ctype === 'tv'"
        column
        justify-center
        align-center
      >
        <!-- Other TVFormats in same TVSpotBundle as thumbnails. -->
        <div
          v-if="bundle && bundle.has_multiple_formats"
          class="mb-2 msr--text"
        >
          <v-layout
            justify-center
            align-center
            class="mb-1 clickable"
            data-cy="toggle-bundle-tvspots-expanded-btn"
            @click="expandallBundleTVSpots = !expandallBundleTVSpots"
          >
            <multiformat-bundle-indicator
              is-multiformat-bundle
              :use-default-margins="false"
              class="mr-2"
            />
            <span class="pseudo-anchor">{{
              $t('bundledContentFormatsHint')
            }}</span>
            <v-icon
              :style="{
                transform: !expandallBundleTVSpots ? '' : 'rotate(180deg)',
              }"
              class="ml-1"
              color="msr"
            >
              keyboard_arrow_down
            </v-icon>
          </v-layout>
          <v-slide-y-transition>
            <div v-if="expandallBundleTVSpots">
              <v-layout
                justify-center
                align-center
                wrap
                class="mt-3 mb-2"
              >
                <div
                  v-for="(tvspot, tvspotIndex) in allBundleTVSpots"
                  :key="tvspotIndex"
                  class="mx-2 clickable"
                  :title="tvspot.tvformat.name"
                  @click="previewedSpot = tvspot"
                >
                  <video-thumbnail
                    :tvspot="tvspot"
                    :outlined="tvspot.id === previewedSpot.id"
                  />
                </div>
              </v-layout>
            </div>
          </v-slide-y-transition>
        </div>
        <!-- Note: To be able to change video players, the rerender-
			  triggering :key *must* be on a parent of the video tag, not the
			  video tag itself, as mejs conflicts with the normal :key behaviour.
        -->
        <div
          v-if="!useIframePreview"
          :key="previewedSpot.id"
          style="position: relative"
        >
          <video
            :id="'player-video-' + previewedSpot.id"
            class="mejs__player contained-poster"
            :width="tvPreviewMaxWidth"
            :height="tvPreviewMaxHeight"
            :poster="videoPosterURL"
            preload="metadata"
            :src="videoSource"
            @contextmenu.prevent
          />
          <span v-if="previewedSpot.tvformat.is_socialmedia_format">
            <img
              v-for="(platformIconUrl, platformIconIndex) in previewedSpot
                .tvformat.socialmedia_icon_urls"
              :key="platformIconUrl"
              :src="platformIconUrl"
              width="32"
              height="32"
              style="position: absolute; left: -16px; z-index: 1"
              :style="{top: -8 + platformIconIndex * 32 + 'px'}"
            >
          </span>
        </div>
        <!-- We rely on default values (defined via tvspot.overlay_options) being shown here. -->
        <div
          v-else
          style="position: relative"
        >
          <div
            v-if="previewedSpot.editable_template"
            class="editable-spot-html-preview-hint"
          >
            {{ $t('preview') }}
          </div>
          <tv-template-preview
            ref="preview"
            :key="previewedSpot.id"
            :html-overlay="previewedSpot.editable_template.html_overlay"
            :background-image="
              !previewedSpot.editable_template
                .use_tvspot_video_for_background &&
                previewedSpot.editable_template
                  .use_tvspot_poster_for_preview_background
                ? previewedSpot.editable_template.video_poster_for_editing
                : null
            "
            :background-video="
              previewedSpot.editable_template.use_tvspot_video_for_background
                ? previewedSpot.preview_background_url
                : null
            "
            :width="previewedSpot.tvformat.width"
            :height="previewedSpot.tvformat.height"
            :max-width="tvPreviewMaxWidth"
            :max-height="tvPreviewMaxHeight"
            :is-multiscreen="previewedSpot.tvformat.is_multiscreen_format"
            :duration="previewedSpot.duration"
            :fake-player-progress="previewedSpot.editable_template.is_animated"
            :fake-fade-in-fade-out="
              previewedSpot.editable_template.fade_enabled
            "
            :fade-color="
              previewedSpot.editable_template.dummy_video_for_background_color
            "
            :fade-in-start="previewedSpot.editable_template.fadein_start"
            :fade-in-stop="previewedSpot.editable_template.fadein_stop"
            :fade-out-start="previewedSpot.editable_template.fadeout_start"
            :fade-out-stop="previewedSpot.editable_template.fadeout_stop"
            class="elevation-3"
            :onload-callback="
              () =>
                $store
                  .dispatch('MAYBE_LIST_BRANDINGS')
                  .then(populateIframeWithInitialOverlayData)
            "
          />
        </div>
        <v-slide-y-transition>
          <!-- Various preview hints -->
          <div
            v-if="previewedSpot.editable_template"
            class="grey--text text--darken-2 mt-2"
            :class="{caption: $vuetify.breakpoint.xs}"
          >
            <span v-if="allEntriesUseSameOverlayData">{{
              $t('editableTemplateSameOverlayDataHint')
            }}</span>
            <span
              v-else
              class="msr--text"
            >{{
              $t('editableTemplateDifferentOverlayDataHint')
            }}</span>
          </div>
          <div
            v-else-if="previewedSpot.has_layoutelements"
            class="msr--text mt-2"
            :class="{caption: $vuetify.breakpoint.xs}"
          >
            {{ $t('editableContentWithLayoutElementsPreviewHint') }}
          </div>
        </v-slide-y-transition>
      </v-layout>
      <!-- RadioSpot Preview -->
      <div
        v-if="previewedSpot.ctype === 'radio'"
        class="audioplayer"
      >
        <div class="mb-3">
          <div id="waveform" />
        </div>
        <audio
          :id="'player-audio-' + previewedSpot.id"
          width="100%"
          preload="metadata"
          :src="audioSource"
        />
      </div>
    </div>
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';

import {importWaveSurfer} from '@/deferredImports';
import {deepCopyObject, getMainTVSpotFromBundle} from '@/utils';

import {
  chooseDefaultBrandingFromBrandings,
  getFallbackBrandingFromTVSpotTemplateData,
} from '@/mixins/tvtemplateBrandings';
import {applyBrandingToOverlayDataBrandableFields} from '@/mixins/overlayOptions.js';

import MultiformatBundleIndicator from '@/components/MultiformatBundleIndicator';
import TvTemplatePreview from '@/components/TvTemplatePreview';
import VideoThumbnail from '@/components/VideoThumbnail';

export default {
  components: {
    MultiformatBundleIndicator,
    TvTemplatePreview,
    VideoThumbnail,
  },
  props: {
    spot: {
      type: Object,
      required: true,
    },
    bundle: {
      type: Object,
      default: null,
    },
    entries: {
      type: Array[Object],
      default: null,
    },
  },
  data() {
    return {
      libWaveSurfer: null,

      audioSource: null,
      expandallBundleTVSpots: false,
      player: null,
      previewedSpot: this.spot,
      allEntriesUseSameOverlayData: true,
      videoSource: null,
    };
  },
  computed: {
    brandings() {
      return this.$store.state.common.brandings;
    },
    defaultBranding() {
      return chooseDefaultBrandingFromBrandings(this.brandings);
    },
    fallbackBranding() {
      return getFallbackBrandingFromTVSpotTemplateData(this.previewedSpot);
    },
    initialBranding() {
      return this.defaultBranding || this.fallbackBranding;
    },
    allBundleTVSpots() {
      if (!this.bundle) {
        return [];
      }

      const mainTVSpot = getMainTVSpotFromBundle(this.bundle);

      /**
       * Main TVSpot is first item always, then by our usual TVFormat order:
       *
       * 16:9, 9:16, 32:9, <everything-else-by-name>
       */
      function getTVSpotOrder(tvspot) {
        if (tvspot == mainTVSpot) {
          return 0;
        }
        if (tvspot.tvformat.aspect_ratio == 16 / 9) {
          return 1;
        }
        if (tvspot.tvformat.aspect_ratio == 9 / 16) {
          return 2;
        }
        if (tvspot.tvformat.aspect_ratio == 32 / 9) {
          return 3;
        }
        return 4;
      }

      return orderBy(this.bundle.tvspots, [getTVSpotOrder, 'tvformat.name']);
    },
    videoPosterURL() {
      const ctype = this.$route.params.ctype;
      if (ctype !== 'tv') {
        return '';
      }
      return this.previewedSpot.poster_url;
    },
    // TODO: See if we can merge this with MiniPreviewVideo -> dialogDimensions()
    /**
     * Use portrait or landscape with a maximum size.
     *
     * Multiscreen clips are always shown as landscape, as we do render
     * special landscape preview videos in the videoworker for them.
     */
    tvPreviewMaxDimensions() {
      const fixedMaxWidth = this.$vuetify.breakpoint.width / 1.5;
      const fixedMaxHeight = this.$vuetify.breakpoint.xsOnly ? 360 : 480;

      const isPortrait =
        this.previewedSpot.height > this.previewedSpot.width &&
        !this.previewedSpot.is_multiscreen;
      const isLandscapeOrOther = !isPortrait;

      let ratio = this.previewedSpot.width / this.previewedSpot.height;
      if (this.previewedSpot.is_multiscreen) {
        ratio = 16.0 / 9.0;
      }

      let calculatedWidth, calculatedHeight;

      if (isPortrait) {
        calculatedHeight = fixedMaxHeight;
        calculatedWidth = calculatedHeight * ratio;
      } else if (isLandscapeOrOther) {
        calculatedWidth = this.$vuetify.breakpoint.width - 48;
        if (calculatedWidth > fixedMaxWidth) {
          calculatedWidth = fixedMaxWidth;
        }
        calculatedHeight = calculatedWidth / ratio;
      }

      if (calculatedHeight > fixedMaxHeight) {
        calculatedHeight = fixedMaxHeight;
        calculatedWidth = calculatedHeight * ratio;
      }

      return {
        width: calculatedWidth,
        height: calculatedHeight,
      };
    },
    tvPreviewMaxWidth() {
      return this.tvPreviewMaxDimensions.width;
    },
    tvPreviewMaxHeight() {
      return this.tvPreviewMaxDimensions.height;
    },
    useIframePreview() {
      return this.previewedSpot.editable_template;
    },
  },
  watch: {
    previewedSpot: function () {
      // The <video> tag is force-refreshed by the Spot swap already,
      // make sure to properly initialize it afterwards.
      this.setupPlayer();

      // Emit a signal so parent component may react to this change.
      this.$emit('changed-previewed-spot', this.previewedSpot);
    },
  },
  mounted() {
    this.expandPreviewDelayed();
  },
  methods: {
    populateIframeWithInitialOverlayData() {
      this.$api
        .getOverlayDataForHtmlPreview(this.previewedSpot)
        .then((response) => {
          this.allEntriesUseSameOverlayData =
            response.data.all_entries_use_same_overlay_data;
          const overlayData = response.data.overlay_data;

          // Inject branding and apply it, if needed.
          if (
            this.previewedSpot.editable_template &&
            this.previewedSpot.editable_template.is_brandable
          ) {
            if (!this.initialBranding) {
              return;
            }
            this.injectBrandingIntoOverlayData(
              this.initialBranding,
              overlayData
            );
            applyBrandingToOverlayDataBrandableFields(
              this.initialBranding,
              this.previewedSpot.overlay_options,
              overlayData
            );
          }

          const previewWidget = this.$refs.preview;
          previewWidget.updateOverlayData(overlayData);
        });
    },
    injectBrandingIntoOverlayData(branding, overlayData) {
      overlayData.branding = deepCopyObject(branding);
    },
    /** We want to see the expansion animation, that's why we delay. */
    expandPreviewDelayed() {
      const vm = this;
      setTimeout(() => {
        vm.expandallBundleTVSpots = true;
      }, 250);
    },
    /**
     * Load sound file via AJAX and visualize as waveforms.
     *
     * Note: Won't load cross-domain, combine the webpack and django
     *   domains with something like nginx to test locally.
     *
     * See:
     *   - https://wavesurfer-js.org/example/video-element/index.html
     *   - https://wavesurfer-js.org/docs/options.html
     */
    initializeWavesurverVisualisation(mediaElement) {
      // Import library chunk if not done yet.
      let importPromise = Promise.resolve();
      if (!this.libWaveSurfer) {
        importPromise = importWaveSurfer().then(
          (lib) => (this.libWaveSurfer = lib)
        );
      }
      importPromise.then(() => {
        const wavesurferInstance = this.libWaveSurfer.create({
          container: '#waveform',
          backend: 'MediaElement',
          responsive: true,
          waveColor: '#b2b2b2',
          progressColor: this.$vuetify.theme.primary,
          cursorColor: this.$vuetify.theme.primary,
          barWidth: 1,
          barRadius: 1,
          barGap: 1,
        });
        wavesurferInstance.load(mediaElement);
      });
    },
    initializePlayer_(ctype, selector) {
      const vm = this;
      const element = document.querySelector(selector);
      this.player = new MediaElementPlayer(element, {
        stretching: 'auto',
        poster: this.videoPosterURL,
        showPosterWhenEnded: true,
        hideVolumeOnTouchDevices: false,
        audioVolume: 'vertical',
        success: function () {
          if (ctype === 'radio') {
            vm.$nextTick(() => {
              vm.initializeWavesurverVisualisation(element);
            });
          }
        },
      });
    },
    setupPlayer() {
      const ctype = this.$route.params.ctype;
      if (ctype === 'radio') {
        this.audioSource = this.previewedSpot.file_url;
        // v-if needs to trigger first.
        this.$nextTick(() => {
          this.initializePlayer_(
            'radio',
            '#player-audio-' + this.previewedSpot.id
          );
        });
      } else if (ctype === 'tv') {
        this.videoSource = this.previewedSpot.preview_url;
        // v-if needs to trigger first.
        this.$nextTick(() => {
          this.initializePlayer_(
            'tv',
            '#player-video-' + this.previewedSpot.id
          );
        });
      }
    },
  },
};
</script>

<style scoped>
.editable-spot-html-preview-hint {
  position: absolute;
  width: 100%;
  bottom: 0;
  z-index: 1;
  font-size: x-large;
  font-weight: bold;
  text-align: center;
  text-transform: uppercase;
  color: white;
  opacity: 0.66;
  pointer-events: none;
  -webkit-text-stroke: 1px black;
  text-stroke: 1px black;
}
@media (max-width: 599px) {
  .editable-spot-html-preview-hint {
    font-size: large;
  }
}
</style>

<style>
@import '../../../node_modules/mediaelement/build/mediaelementplayer.min.css';

/* When zoomed in, this color may flash through as 1px line, so match app background. */
.mejs__player.contained-poster {
  background-color: white;
}

/* Make sure poster is contained in the player, otherwise it would be
   blown out to fully cover the page, which looks weird for portrait. */
.mejs__player.contained-poster
  .mejs__inner
  .mejs__layers
  .mejs__poster.mejs__layer {
  background-size: contain;
}
</style>

<style scoped>
.audioplayer {
  width: 500px;
}

@media (max-width: 599px) {
  /* Use full width on small devices */
  .audioplayer {
    width: 100%;
  }
}

.pseudo-anchor {
  text-decoration: underline;
  cursor: pointer;
}

/* https://una.im/vignettes/ */
.vignette-shadow {
  box-shadow: inset 0 0 64px rgba(0, 0, 0, 0.2);
}
</style>



// WEBPACK FOOTER //
// src/components/SpotDetail/SpotDetailPreview.vue